import {
  Archive,
  BrainCircuit,
  Bell,
  Building2,
  CircleHelp,
  Dna,
  History,
  House,
  Landmark,
  LayoutDashboard,
  Layers,
  Map as MapIcon,
  MessageSquareWarning,
  PawPrint,
  Signpost,
  Tags,
  Tent,
  UserCheck,
  UserCog,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { Cow, Farmer } from '@/components/icons';
import { MASTERS, masterPath, type MasterKey } from '@/features/masters/masters.config';
import {
  CLIENT_USER_TYPES,
  PLATFORM_USER_TYPES,
  Permission,
  UserType,
  type Permission as PermissionCode,
} from '@/types/auth';
import type { AccessRule } from '@/utils/permissions';
import { paths } from './paths';

/**
 * One entry per authenticated screen. The router guards each route with `access`, and the
 * sidebar shows only the screens the user may open — both read this single table.
 */
export interface Screen {
  path: string;
  label: string;
  icon: LucideIcon;
  access: AccessRule;
}

const platform = (...permissions: PermissionCode[]): AccessRule => ({
  userTypes: PLATFORM_USER_TYPES,
  permissions,
});
const client = (...permissions: PermissionCode[]): AccessRule => ({
  userTypes: CLIENT_USER_TYPES,
  permissions,
});

export const SCREENS = {
  dashboard: {
    path: paths.dashboard,
    label: 'Dashboard',
    icon: LayoutDashboard,
    access: { permissions: [Permission.DASHBOARD_VIEW] },
  },
  adminUsers: {
    path: paths.adminUsers,
    label: 'Admin Users',
    icon: UserCog,
    access: platform(Permission.ADMIN_USERS_READ),
  },
  clientUsers: {
    path: paths.clientUsers,
    label: 'Client Users',
    icon: Building2,
    access: platform(Permission.CLIENTS_READ),
  },
  loginLogs: {
    path: paths.loginLogs,
    label: 'Login Logs',
    icon: History,
    access: platform(Permission.LOGIN_LOGS_READ),
  },
  /** Every AIML call and its scores: the Super Admin's only. */
  mlLogs: {
    path: paths.mlLogs,
    label: 'ML Logs',
    icon: BrainCircuit,
    access: { userTypes: [UserType.SUPER_ADMIN], permissions: [Permission.ML_LOGS_READ] },
  },
  faqs: {
    path: paths.faqs,
    label: 'FAQs',
    icon: CircleHelp,
    access: platform(Permission.FAQS_READ),
  },
  /** Every client's farmers for the Super Admin, a Client Admin's own. */
  farmers: {
    path: paths.farmers,
    label: 'Farmers',
    icon: Farmer,
    access: { permissions: [Permission.FARMERS_READ] },
  },
  /** Every client's animals for the Super Admin and QC, a Client Admin's own. */
  animals: {
    path: paths.animals,
    label: 'Animals',
    icon: Cow,
    access: { permissions: [Permission.ANIMALS_READ] },
  },
  /** Every client's reports for the Super Admin and QC, a Client Admin's own (read only). */
  grievances: {
    path: paths.grievances,
    label: 'Grievance Reports',
    icon: MessageSquareWarning,
    access: { permissions: [Permission.GRIEVANCES_READ] },
  },
  /** AP's Push Notifications: the Super Admin's messages to app users. */
  notifications: {
    path: paths.notifications,
    label: 'Push Notifications',
    icon: Bell,
    access: { userTypes: [UserType.SUPER_ADMIN], permissions: [Permission.NOTIFICATIONS_SEND] },
  },
  /** AP's Archive Grievances: the Super Admin's only. */
  grievanceArchive: {
    path: paths.grievanceArchive,
    label: 'Archive Grievances',
    icon: Archive,
    access: { userTypes: [UserType.SUPER_ADMIN], permissions: [Permission.GRIEVANCES_READ] },
  },
  /** The lists' CSV files (Download buttons): Super Admin and Client Admin. */
  administrators: {
    path: paths.administrators,
    label: 'Administrators',
    icon: UserCheck,
    access: client(Permission.FIELD_ADMINS_READ),
  },
  agents: {
    path: paths.agents,
    label: 'Agents',
    icon: Users,
    access: client(Permission.AGENTS_READ),
  },
} as const satisfies Record<string, Screen>;

const MASTER_ICONS: Record<MasterKey, LucideIcon> = {
  'animal-categories': Tags,
  'animal-types': PawPrint,
  'animal-breeds': Dna,
  states: MapIcon,
  districts: Landmark,
  mandals: Signpost,
  villages: House,
  hamlets: Tent,
};

export const MASTER_SCREENS: readonly (Screen & { key: MasterKey })[] = MASTERS.map((m) => ({
  key: m.key,
  path: masterPath(m.key),
  label: m.label,
  icon: MASTER_ICONS[m.key],
  access: platform(Permission.MASTERS_READ),
}));

export const MASTERS_GROUP_ICON = Layers;
